import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CrudStyles } from '../shared/Crud';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 16px;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
`;

const ModalBox = styled.div`
    box-sizing: border-box;
    background: white;
    padding: 24px;
    border-radius: 12px;
    width: 100%;
    max-width: min(440px, calc(100vw - 32px));
    max-height: min(90vh, 90dvh);
    overflow-y: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const Title = styled.h2`
    margin: 0 0 12px;
    font-size: 18px;
    color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};
`;

const Message = styled.p`
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme?.colors?.gray?.[900] || '#444'};
`;

const Options = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 24px;
`;

const OptionLabel = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
    border: 1px solid ${({ theme, $selected }) =>
        $selected
            ? theme?.colors?.primary?.main || '#3b5998'
            : theme?.colors?.gray?.[200] || '#e5e5e5'};
    border-radius: 8px;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    background: ${({ $selected }) => ($selected ? 'rgba(59, 89, 152, 0.06)' : '#fff')};
    opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

    input {
        margin-top: 2px;
        flex-shrink: 0;
    }
`;

const OptionText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 14px;
    color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};

    small {
        font-size: 12px;
        color: ${({ theme }) => theme?.colors?.gray?.[600] || '#666'};
        line-height: 1.4;
    }
`;

const Actions = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

const { Button } = CrudStyles;

/**
 * Modal para escolher se o protocolo de entrega inclui informação financeira.
 */
export default function DeliveryProtocolPrintModal({
    open,
    isSubmitting = false,
    shareFile = null,
    onClose,
    onConfirm,
    onShare,
}) {
    const [includeValue, setIncludeValue] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const shareButtonRef = useRef(null);
    const onShareRef = useRef(onShare);
    const sharingRef = useRef(false);
    const readyToShare = Boolean(shareFile);

    onShareRef.current = onShare;

    useEffect(() => {
        if (open) {
            setIncludeValue(false);
            setIsSharing(false);
            sharingRef.current = false;
        }
    }, [open]);

    useEffect(() => {
        const node = shareButtonRef.current;
        if (!readyToShare || !node) return undefined;

        const handleClick = () => {
            if (sharingRef.current) return;
            const pending = onShareRef.current?.();
            sharingRef.current = true;
            setIsSharing(true);
            Promise.resolve(pending).finally(() => {
                sharingRef.current = false;
                setIsSharing(false);
            });
        };

        node.addEventListener('click', handleClick);
        return () => node.removeEventListener('click', handleClick);
    }, [readyToShare, shareFile]);

    if (!open) return null;

    const handleConfirm = () => {
        onConfirm?.(includeValue);
    };

    const busy = isSubmitting || isSharing;

    return (
        <Overlay
            role="dialog"
            aria-modal="true"
            aria-labelledby="delivery-protocol-print-title"
            onClick={(e) => !busy && e.target === e.currentTarget && onClose?.()}
        >
            <ModalBox onClick={(e) => e.stopPropagation()}>
                <Title id="delivery-protocol-print-title">
                    {readyToShare ? 'Protocolo pronto' : 'Imprimir protocolo de entrega'}
                </Title>
                {readyToShare ? (
                    <Message>
                        O PDF está pronto. Toque em Compartilhar para enviá-lo pelo WhatsApp ou por
                        outro aplicativo.
                    </Message>
                ) : (
                    <>
                        <Message>
                            Escolha se o PDF deve incluir o valor total da cobrança da entrega.
                        </Message>
                        <Options>
                            <OptionLabel $selected={!includeValue} $disabled={isSubmitting}>
                                <input
                                    type="radio"
                                    name="includeValue"
                                    checked={!includeValue}
                                    disabled={isSubmitting}
                                    onChange={() => setIncludeValue(false)}
                                />
                                <OptionText>
                                    Sem informação financeira
                                    <small>O protocolo será emitido sem o valor da cobrança.</small>
                                </OptionText>
                            </OptionLabel>
                            <OptionLabel $selected={includeValue} $disabled={isSubmitting}>
                                <input
                                    type="radio"
                                    name="includeValue"
                                    checked={includeValue}
                                    disabled={isSubmitting}
                                    onChange={() => setIncludeValue(true)}
                                />
                                <OptionText>
                                    Com informação financeira
                                    <small>
                                        Inclui o valor total da cobrança no PDF, quando houver cobrança
                                        disponível.
                                    </small>
                                </OptionText>
                            </OptionLabel>
                        </Options>
                    </>
                )}
                <Actions>
                    {readyToShare ? (
                        <>
                            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                                Fechar
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                ref={shareButtonRef}
                                disabled={busy}
                            >
                                {isSharing ? 'Abrindo…' : 'Compartilhar'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button type="button" variant="primary" onClick={handleConfirm} disabled={isSubmitting}>
                                {isSubmitting ? 'Gerando…' : 'Emitir protocolo'}
                            </Button>
                        </>
                    )}
                </Actions>
            </ModalBox>
        </Overlay>
    );
}
